import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Secret.css';

const bootSequence = [
    '> UNAUTHORIZED ACCESS POINT LOCATED',
    '> TRACING CONNECTION... FAILED',
    '> IDENTITY UNKNOWN. WELCOME, STRANGER.',
    "> TYPE 'HELP' AND PRESS ENTER"
];

const MATRIX_CHARS = 'ROTB01アイウエオカキクケコサシスセソ$#@%&';

// Falling character rain in the background
function MatrixRain() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationId;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const fontSize = 14;
        const columns = () => Math.floor(canvas.width / fontSize);
        let drops = new Array(columns()).fill(1);

        let lastTime = 0;
        const draw = (time) => {
            animationId = requestAnimationFrame(draw);
            if (time - lastTime < 50) return;
            lastTime = time;

            if (drops.length !== columns()) {
                drops = new Array(columns()).fill(1);
            }

            ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = 'rgba(0, 255, 0, 0.35)';
            ctx.font = `${fontSize}px monospace`;

            drops.forEach((y, i) => {
                const char = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
                ctx.fillText(char, i * fontSize, y * fontSize);
                if (y * fontSize > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0;
                }
                drops[i]++;
            });
        };
        animationId = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return <canvas ref={canvasRef} className="matrix-canvas" />;
}

function Secret() {
    const navigate = useNavigate();
    const [lines, setLines] = useState([]);
    const [input, setInput] = useState('');
    const [booted, setBooted] = useState(false);
    const [awaitingPasscode, setAwaitingPasscode] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const inputRef = useRef(null);
    const bodyRef = useRef(null);

    // Boot lines appear one by one
    useEffect(() => {
        const timers = bootSequence.map((line, i) =>
            setTimeout(() => {
                setLines(prev => [...prev, line]);
                if (i === bootSequence.length - 1) setBooted(true);
            }, 700 * (i + 1))
        );
        return () => timers.forEach(clearTimeout);
    }, []);

    // Keep terminal scrolled to the newest line
    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [lines]);

    const print = (...output) => setLines(prev => [...prev, ...output]);

    const handleCommand = (raw) => {
        const cmd = raw.trim().toLowerCase();

        if (awaitingPasscode) {
            print(`> ${'*'.repeat(raw.length)}`);
            if (cmd === 'basement') {
                setAwaitingPasscode(false);
                print('  DECRYPTING FILE ██████████ 100%', '  ACCESS GRANTED.');
                setTimeout(() => setUnlocked(true), 1000);
            } else {
                print('  ACCESS DENIED.', '  HINT: WHERE DID IT ALL BEGIN?');
            }
            return;
        }

        print(`> ${raw}`);

        switch (cmd) {
            case 'help':
                print(
                    '  AVAILABLE COMMANDS:',
                    '  WHOAMI ....... IDENTIFY YOURSELF',
                    '  PHASES ....... LIST ALL KNOWN EVENTS',
                    '  CREW ......... THE ONES WHO BUILT THIS',
                    '  DECRYPT ...... OPEN CLASSIFIED FILE [LOCKED]',
                    '  CLEAR ........ WIPE TERMINAL',
                    '  EXIT ......... DISCONNECT'
                );
                break;
            case 'whoami':
                print(`  GUEST-${Math.floor(1000 + Math.random() * 9000)} // CLEARANCE LEVEL: NONE`, '  YOU WERE NEVER HERE.');
                break;
            case 'phases':
                print(
                    '  30.06.23  PHASE 1  GENESIS',
                    '  23.02.24  PHASE 2  MOMENTUM',
                    '  06.07.24  PHASE 3  SOLSTICE',
                    '  11.10.24  PHASE 4  DIMENSIONS',
                    '  28.02.25  PHASE 5  REFLECTIONS',
                    '  31.12.25  PHASE 6  ANCHOR NYE',
                    '  14.03.25  PHASE 7  ASCENSION',
                    '  ██.██.██  PHASE 8  [ENCRYPTED — USE DECRYPT]'
                );
                break;
            case 'crew':
                print(
                    '  THE BASEMENT WAS BUILT BY:',
                    '  > ALEC ..... STRUCTURAL INTEGRITY',
                    '  > WOUT ..... SIGNAL & NOISE',
                    '  > THIJS .... LOGISTICS & LEGEND',
                    '  > LUCAS .... FRONTLINE OPERATIONS',
                    '  RESPECT IS DUE.'
                );
                break;
            case 'decrypt':
                setAwaitingPasscode(true);
                print('  CLASSIFIED FILE: PHASE_8.DAT', '  ENTER PASSCODE:');
                break;
            case 'clear':
                setLines([]);
                break;
            case 'exit':
                print('  DISCONNECTING...');
                setTimeout(() => navigate('/'), 800);
                break;
            case '':
                break;
            default:
                print(`  COMMAND NOT RECOGNIZED: ${cmd.toUpperCase()}`, "  TYPE 'HELP' FOR A LIST OF COMMANDS");
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCommand(input);
            setInput('');
        }
    };

    return (
        <div className="secret-page" onClick={() => inputRef.current?.focus()}>
            <MatrixRain />

            <div className="secret-terminal">
                <div className="secret-terminal-header">
                    <span>ROTB://RESTRICTED_SHELL</span>
                    <span className="secret-terminal-status">● LIVE</span>
                </div>
                <div className="secret-terminal-body" ref={bodyRef}>
                    {lines.map((line, index) => (
                        <div key={index} className="secret-line">{line}</div>
                    ))}
                    {booted && !unlocked && (
                        <div className="secret-line secret-prompt">
                            {'> '}
                            <input
                                ref={inputRef}
                                type={awaitingPasscode ? 'password' : 'text'}
                                className="secret-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKeyDown}
                                autoFocus
                                autoComplete="off"
                                autoCapitalize="off"
                                spellCheck="false"
                            />
                        </div>
                    )}
                </div>
                <div className="secret-scanlines"></div>
            </div>

            {/* Phase 8 reveal */}
            {unlocked && (
                <div className="secret-overlay" onClick={() => setUnlocked(false)}>
                    <div className="secret-glitch" data-text="PHASE 8">PHASE 8</div>
                    <div className="secret-reveal-sub">SIGNAL INTERCEPTED — THE BASEMENT WILL RISE AGAIN</div>
                    <div className="secret-reveal-hint">KEEP THIS BETWEEN US. [CLICK TO BURN FILE]</div>
                </div>
            )}

            <div className="secret-navigation">
                <button className="home-btn" onClick={() => navigate('/')}>
                    HOME
                </button>
            </div>
        </div>
    );
}

export default Secret;
